import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, delay, map, mergeMap, of, retry, tap } from 'rxjs';

import { Administration, DepreciationSync, Division, ProgressModel } from '@/depreciations/models';
import { PollingStatus } from '@/tax/models';

import { environment } from '@env';

const DELAY_STATUS_FETCH_MS = 1000;

const invalidErrorMessage = 'Not ready';

@Injectable({
    providedIn: 'root',
})
export class DepreciationService {
    constructor(private http: HttpClient) {}

    createDepreciationSyncAndPollResult(
        orgId: number,
        companyId: number,
        depreciationSync: DepreciationSync,
        progress: ProgressModel,
    ): Observable<DepreciationSync> {
        return this.createDepreciationSync(orgId, companyId, depreciationSync).pipe(
            delay(DELAY_STATUS_FETCH_MS),
            mergeMap((newDepreciationSync) =>
                this.getDepreciationSync(orgId, companyId, newDepreciationSync.id).pipe(
                    tap((sync) => {
                        progress.progress = sync.progress;
                        if (sync.status === PollingStatus.Init || sync.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    getDepreciationSync(orgId: number, companyId: number, depreciationSyncId: number): Observable<DepreciationSync> {
        return this.http
            .get<any>(environment.endpoints.organisationCompaniesDepreciationSyncsDetails(orgId, companyId, depreciationSyncId))
            .pipe(map<any, DepreciationSync>((depreciationSync) => new DepreciationSync().deserialize(depreciationSync)));
    }

    createDepreciationSync(orgId: number, companyId: number, depreciationSync: DepreciationSync): Observable<DepreciationSync> {
        return this.http
            .post<DepreciationSync>(
                environment.endpoints.organisationCompaniesDepreciationSyncsList(orgId, companyId),
                depreciationSync.serialize(),
            )
            .pipe(map((sync) => new DepreciationSync().deserialize(sync)));
    }

    getDivisions(orgId: number): Observable<Division[]> {
        return this.http
            .get<any>(environment.endpoints.organisationDivisions(orgId))
            .pipe(map<any, Division[]>((divisions) => divisions.map((d: Division) => new Division().deserialize(d))));
    }

    getDivision(orgId: number, divisionId: string): Observable<Division> {
        return this.http
            .get<any>(environment.endpoints.organisationDivision(orgId, divisionId))
            .pipe(map<any, Division>((division) => new Division().deserialize(division)));
    }

    getAdministrations(orgId: number): Observable<Administration[]> {
        return this.http
            .get<any>(environment.endpoints.organisationAdministrations(orgId))
            .pipe(map<any, Administration[]>((administrations) => administrations.map((d: Administration) => new Administration().deserialize(d))));
    }
}
